import React from "react";
import Seo from "components/shared/Seo";

const PrivacyPolicy = () => {
    return (
        <>
            <Seo title="Privacy Policy" />
            <h1 className="text-center my-3 mb-4">Privacy Policy</h1>
            <p className="text-muted">
                <small>Last updated: August 7, 2025</small>
            </p>

            <section className="mb-4">
                <h2>1. Introduction</h2>
                <p>
                    Welcome to Wishliz ("we," "our," or "us"). We are committed
                    to protecting your privacy and personal data. This Privacy
                    Policy explains how we collect, use, disclose, and safeguard
                    your information when you use our wishlist management
                    service.
                </p>
            </section>

            <section className="mb-4">
                <h2>2. Information We Collect</h2>
                <h4>2.1 Personal Information</h4>
                <p>We may collect the following personal information:</p>
                <ul>
                    <li>Name and email address when you create an account</li>
                    <li>Profile information you choose to provide</li>
                    <li>
                        Wishlist items and descriptions you add to our service
                    </li>
                    <li>Communication preferences</li>
                </ul>

                <h4>2.2 Usage Information</h4>
                <p>
                    We automatically collect certain information about your use
                    of our service:
                </p>
                <ul>
                    <li>Device information (browser type, operating system)</li>
                    <li>Log data (IP address, access times, pages viewed)</li>
                    <li>Usage patterns and preferences</li>
                    <li>Cookies and similar tracking technologies</li>
                </ul>
            </section>

            <section className="mb-4">
                <h2>3. How We Use Your Information</h2>
                <p>We use your information to:</p>
                <ul>
                    <li>Provide and maintain our wishlist service</li>
                    <li>Create and manage your user account</li>
                    <li>Process your subscription and payments</li>
                    <li>
                        Send you important service updates and notifications
                    </li>
                    <li>Improve our service and user experience</li>
                    <li>Provide customer support</li>
                    <li>Comply with legal obligations</li>
                </ul>
            </section>

            <section className="mb-4">
                <h2>4. Information Sharing and Disclosure</h2>
                <p>
                    We do not sell, trade, or rent your personal information to
                    third parties. We may share your information only in the
                    following circumstances:
                </p>
                <ul>
                    <li>
                        <strong>Service Providers:</strong> With trusted
                        third-party service providers who assist in operating
                        our service
                    </li>
                    <li>
                        <strong>Legal Requirements:</strong> When required by
                        law or to protect our rights and safety
                    </li>
                    <li>
                        <strong>Business Transfer:</strong> In connection with a
                        merger, acquisition, or sale of assets
                    </li>
                    <li>
                        <strong>Consent:</strong> When you explicitly consent to
                        the sharing
                    </li>
                </ul>
            </section>

            <section className="mb-4">
                <h2>5. Data Security</h2>
                <p>
                    We implement appropriate technical and organizational
                    security measures to protect your personal information
                    against unauthorized access, alteration, disclosure, or
                    destruction. However, no method of transmission over the
                    Internet is 100% secure.
                </p>
            </section>

            <section className="mb-4">
                <h2>6. Data Retention</h2>
                <p>
                    We retain your personal information for as long as necessary
                    to provide our services and fulfill the purposes outlined in
                    this Privacy Policy, unless a longer retention period is
                    required by law.
                </p>
            </section>

            <section className="mb-4">
                <h2>7. Your Rights</h2>
                <p>
                    Depending on your location, you may have the following
                    rights regarding your personal information:
                </p>
                <ul>
                    <li>Access to your personal data</li>
                    <li>Correction of inaccurate data</li>
                    <li>Deletion of your data</li>
                    <li>Data portability</li>
                    <li>Objection to processing</li>
                    <li>Withdrawal of consent</li>
                </ul>
                <p>
                    To exercise these rights, please contact us using the
                    information provided below.
                </p>
            </section>

            <section className="mb-4">
                <h2>8. Cookies and Tracking Technologies</h2>
                <p>
                    We use cookies and similar tracking technologies to enhance
                    your experience on our service. You can control cookie
                    preferences through your browser settings, though disabling
                    cookies may affect service functionality.
                </p>
            </section>

            <section className="mb-4">
                <h2>9. Third-Party Links</h2>
                <p>
                    Our service may contain links to third-party websites. We
                    are not responsible for the privacy practices of these
                    external sites and encourage you to review their privacy
                    policies.
                </p>
            </section>

            <section className="mb-4">
                <h2>10. Children's Privacy</h2>
                <p>
                    Our service is not intended for children under 13 years of
                    age. We do not knowingly collect personal information from
                    children under 13. If we become aware of such collection, we
                    will take steps to delete the information promptly.
                </p>
            </section>

            <section className="mb-4">
                <h2>11. International Data Transfers</h2>
                <p>
                    Your information may be transferred to and processed in
                    countries other than your country of residence. We ensure
                    appropriate safeguards are in place for such transfers.
                </p>
            </section>

            <section className="mb-4">
                <h2>12. Changes to This Privacy Policy</h2>
                <p>
                    We may update this Privacy Policy from time to time. We will
                    notify you of any material changes by posting the new
                    Privacy Policy on this page and updating the "Last updated"
                    date.
                </p>
            </section>

            <section className="mb-4">
                <h2>13. Contact Us</h2>
                <p>
                    If you have any questions about this Privacy Policy or our
                    privacy practices, please contact us at:
                </p>
                <p>
                    <strong>Email:</strong>{" "}
                    <a
                        href="mailto:support@wishliz.com"
                        className="text-primary fw-medium"
                    >
                        support@wishliz.com
                    </a>
                </p>
            </section>

            <hr className="my-5" />
            <p className="text-muted text-center">
                <small>
                    This Privacy Policy is designed to be comprehensive while
                    remaining user-friendly. Please review it carefully and
                    contact us if you have any questions.
                </small>
            </p>
        </>
    );
};

export default PrivacyPolicy;
